import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { HeroMark } from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { Loader2, Mail, Lock, User as UserIcon } from "lucide-react";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 5.1 29.6 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 5.1 29.6 3 24 3 16 3 9.1 7.6 6.3 14.7z"/><path fill="#4CAF50" d="M24 45c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 36 26.7 37 24 37c-5.3 0-9.7-2.6-11.3-6.9l-6.5 5C9.1 42.3 16 45 24 45z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.9 36.2 45 30.6 45 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
);

const Auth = () => {
  const { login, register, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/dashboard";

  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [regData, setRegData] = useState({ name: "", email: "", password: "" });

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success("Welcome back, traveler!");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(regData.name, regData.email, regData.password);
      toast.success("Account created — welcome aboard!");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div className="hidden lg:block">
          <HeroMark />
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-6">{t("auth.join")}</h1>
          <p className="text-secondary-cosmic mt-3 max-w-md">{t("auth.joinDesc")}</p>
        </div>

        <Card className="glass p-6 sm:p-8 w-full max-w-md mx-auto">
          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 bg-white/5 w-full">
              <TabsTrigger value="login" data-testid="auth-tab-login">{t("auth.login")}</TabsTrigger>
              <TabsTrigger value="register" data-testid="auth-tab-register">{t("auth.signup")}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4 mt-4">
                <div>
                  <Label className="text-secondary-cosmic">Email</Label>
                  <div className="relative mt-1"><Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#22D3EE]" />
                    <Input data-testid="auth-email-input" type="email" required value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} placeholder="you@galaxy.com" className="pl-9 bg-white/5 border-white/10 text-white h-11" />
                  </div>
                </div>
                <div>
                  <Label className="text-secondary-cosmic">Password</Label>
                  <div className="relative mt-1"><Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#22D3EE]" />
                    <Input data-testid="auth-password-input" type="password" required value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} placeholder="••••••••" className="pl-9 bg-white/5 border-white/10 text-white h-11" />
                  </div>
                </div>
                <Button type="submit" data-testid="auth-submit-button" disabled={loading} className="w-full h-11 bg-[hsl(var(--primary))] text-white glow-violet hover:glow-cyan">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : t("auth.loginBtn")}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4 mt-4">
                <div>
                  <Label className="text-secondary-cosmic">Full name</Label>
                  <div className="relative mt-1"><UserIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#22D3EE]" />
                    <Input data-testid="auth-name-input" required value={regData.name} onChange={(e) => setRegData({ ...regData, name: e.target.value })} placeholder="Nova Starling" className="pl-9 bg-white/5 border-white/10 text-white h-11" />
                  </div>
                </div>
                <div>
                  <Label className="text-secondary-cosmic">Email</Label>
                  <div className="relative mt-1"><Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#22D3EE]" />
                    <Input required type="email" value={regData.email} onChange={(e) => setRegData({ ...regData, email: e.target.value })} placeholder="you@galaxy.com" className="pl-9 bg-white/5 border-white/10 text-white h-11" />
                  </div>
                </div>
                <div>
                  <Label className="text-secondary-cosmic">Password</Label>
                  <div className="relative mt-1"><Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#22D3EE]" />
                    <Input required type="password" value={regData.password} onChange={(e) => setRegData({ ...regData, password: e.target.value })} placeholder="Create a password" className="pl-9 bg-white/5 border-white/10 text-white h-11" />
                  </div>
                </div>
                <Button type="submit" data-testid="auth-register-submit" disabled={loading} className="w-full h-11 bg-[hsl(var(--primary))] text-white glow-violet hover:glow-cyan">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : t("auth.createBtn")}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-3 my-5"><div className="h-px bg-white/10 flex-1" /><span className="text-xs text-muted-cosmic">or</span><div className="h-px bg-white/10 flex-1" /></div>
          <Button data-testid="auth-google-button" type="button" onClick={loginWithGoogle} variant="outline" className="w-full h-11 bg-white/5 border-white/10 text-white hover:bg-white/8 gap-2">
            <GoogleIcon /> {t("auth.google")}
          </Button>
          <p className="text-xs text-muted-cosmic text-center mt-4">By continuing you agree to our cosmic Terms &amp; Privacy.</p>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
